import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, type Charger, type ChargerInput } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, type Column } from '@/components/ui';
import { IconMap } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';
import { Drawer, Field } from '@/components/admin/Drawer';
import { useAdminPermission } from '@/lib/useAdminPermission';

/** Chargers — the EV charging stations a driver can find between trips. Telride
 *  runs an exclusively-Tesla (electric) fleet, so drivers need a curated map of
 *  where to charge. Staff with cms.manage author the list (name + centre +
 *  network/connector/stalls); the driver app lists the ACTIVE ones, sorted by
 *  distance, with a "Navigate" hand-off. admin-svc owns the `chargers` table
 *  directly (like docks). Read-only roles still see the list. */
export function Chargers() {
  const [editing, setEditing] = useState<Charger | 'new' | null>(null);
  const canManage = useAdminPermission('cms.manage');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'chargers'],
    queryFn: ({ signal }) => adminApi.chargers(signal),
  });

  const chargers = data?.chargers ?? [];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Chargers"
        subtitle="EV charging stations the driver app shows drivers between trips. Add the stations near your service area; drivers see the active ones sorted by distance."
      />

      {canManage ? (
        <div className="mb-3 flex justify-end">
          <button type="button" onClick={() => setEditing('new')} className="btn-primary">
            New charger
          </button>
        </div>
      ) : null}

      <Card flush>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner label="Loading chargers…" />
          </div>
        ) : isError ? (
          <EmptyState title="Couldn't load chargers" description={(error as Error)?.message} />
        ) : (
          <ChargersTable rows={chargers} canManage={canManage} onEdit={(c) => setEditing(c)} />
        )}
      </Card>

      {editing ? <ChargerDrawer charger={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </AdminPage>
  );
}

/** The chargers DataTable — id / name / network / centre / stalls / status, plus
 *  (for cms.manage) Edit + Delete. */
function ChargersTable({ rows, canManage, onEdit }: { rows: Charger[]; canManage: boolean; onEdit: (c: Charger) => void }) {
  const qc = useQueryClient();
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const del = useMutation({
    mutationFn: (id: number) => adminApi.deleteCharger(id),
    onMutate: (id) => setDeletingId(id),
    onSettled: () => {
      setDeletingId(null);
      qc.invalidateQueries({ queryKey: ['admin', 'chargers'] });
    },
  });

  const columns: Array<Column<Charger>> = [
    { key: 'id', header: 'ID', cell: (r) => <span className="metric text-xs text-slate-400">#{r.id}</span> },
    { key: 'name', header: 'Name', cell: (r) => <span className="font-medium text-slate-100">{r.name}</span> },
    { key: 'network', header: 'Network', cell: (r) => <span className="text-xs text-slate-300">{r.network || '—'}</span> },
    {
      key: 'centre',
      header: 'Centre',
      cell: (r) => (
        <span className="metric text-xs text-slate-300">
          {r.lat.toFixed(4)}, {r.lng.toFixed(4)}
        </span>
      ),
    },
    { key: 'stalls', header: 'Stalls', cell: (r) => <span className="text-xs text-slate-300">{r.stalls || '—'}</span> },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <Badge tone={r.active ? 'ok' : 'neutral'} dot>
          {r.active ? 'active' : 'inactive'}
        </Badge>
      ),
    },
  ];
  if (canManage) {
    columns.push({
      key: 'actions',
      header: '',
      align: 'right',
      cell: (r) => (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => onEdit(r)}
            className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
          >
            Edit
          </button>
          <button
            type="button"
            disabled={del.isPending && deletingId === r.id}
            onClick={() => {
              if (window.confirm(`Delete charger "${r.name}"? This can't be undone.`)) del.mutate(r.id);
            }}
            className="rounded-md border border-ink-700 px-2 py-1 text-xs text-danger hover:bg-ink-800 disabled:opacity-50"
          >
            {del.isPending && deletingId === r.id ? '…' : 'Delete'}
          </button>
        </div>
      ),
    });
  }

  return (
    <DataTable<Charger>
      columns={columns}
      rows={rows}
      rowKey={(r) => String(r.id)}
      stickyHeader
      empty={<EmptyState title="No chargers" description="No charging stations yet — add one so drivers can find a charger between trips." icon={<IconMap className="h-6 w-6" />} />}
    />
  );
}

// ─── charger authoring drawer ───────────────────────────────────

interface FormState {
  name: string;
  active: boolean;
  lat: string;
  lng: string;
  address: string;
  network: string;
  connector: string;
  stalls: string;
  notes: string;
}

/** ChargerDrawer — the create/edit form. charger=null ⇒ create; else edit. */
function ChargerDrawer({ charger, onClose }: { charger: Charger | null; onClose: () => void }) {
  const isEdit = charger !== null;
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(() => seedForm(charger));
  const [clientError, setClientError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (input: ChargerInput) => (isEdit ? adminApi.updateCharger(charger!.id, input) : adminApi.createCharger(input)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'chargers'] });
      onClose();
    },
  });

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = () => {
    setClientError(null);
    const built = buildInput(form);
    if ('error' in built) {
      setClientError(built.error);
      return;
    }
    mutation.mutate(built.input);
  };

  return (
    <Drawer
      title={isEdit ? `Edit charger #${charger!.id}` : 'New charger'}
      subtitle={isEdit ? charger!.name : 'Define an EV charging station'}
      onClose={onClose}
    >
      <div className="space-y-4">
        {isEdit ? (
          <div>
            <Field label="ID" value={`#${charger!.id}`} />
            <Field label="Status" value={charger!.active ? 'Active' : 'Inactive'} />
          </div>
        ) : null}

        <Card>
          <div className="space-y-3">
            <TextField label="Name" hint="The station name drivers see (e.g. Tesla Supercharger — Zetland)." value={form.name} onChange={(v) => set('name', v)} placeholder="Tesla Supercharger — Zetland" />
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Latitude" hint="-90 to 90" value={form.lat} onChange={(v) => set('lat', v)} type="number" placeholder="-33.9061" />
              <TextField label="Longitude" hint="-180 to 180" value={form.lng} onChange={(v) => set('lng', v)} type="number" placeholder="151.2070" />
            </div>
            <TextField label="Address" hint="Optional — shown to drivers." value={form.address} onChange={(v) => set('address', v)} placeholder="Zetland NSW 2017" />
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Network" hint="e.g. Tesla Supercharger" value={form.network} onChange={(v) => set('network', v)} placeholder="Tesla Supercharger" />
              <TextField label="Connector" hint="e.g. Tesla / CCS2" value={form.connector} onChange={(v) => set('connector', v)} placeholder="Tesla / CCS2" />
            </div>
            <TextField label="Stalls" hint="Number of charging bays." value={form.stalls} onChange={(v) => set('stalls', v)} type="number" placeholder="8" />
            <TextField label="Notes" hint="Optional — access hints, hours, etc." value={form.notes} onChange={(v) => set('notes', v)} placeholder="24/7, behind the shopping centre" />
            <label className="flex items-center gap-2 pt-1 text-sm text-slate-200">
              <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} className="h-4 w-4 rounded border-ink-600 bg-ink-900" />
              Active (shown to drivers in the app)
            </label>
          </div>
        </Card>

        <div className="flex items-center gap-3">
          <button type="button" disabled={mutation.isPending} onClick={onSubmit} className="btn-primary">
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create charger'}
          </button>
          <button type="button" disabled={mutation.isPending} onClick={onClose} className="btn-secondary">
            Cancel
          </button>
        </div>
        {clientError ? <p className="text-xs text-danger">{clientError}</p> : null}
        {mutation.isError ? <p className="text-xs text-danger">{(mutation.error as Error)?.message}</p> : null}
      </div>
    </Drawer>
  );
}

function TextField({
  label,
  hint,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'number';
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-[11px] text-muted">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1 input" />
      {hint ? <p className="mt-0.5 text-[11px] text-muted">{hint}</p> : null}
    </div>
  );
}

function seedForm(c: Charger | null): FormState {
  if (!c) return { name: '', active: true, lat: '', lng: '', address: '', network: '', connector: '', stalls: '', notes: '' };
  return {
    name: c.name,
    active: c.active,
    lat: String(c.lat),
    lng: String(c.lng),
    address: c.address ?? '',
    network: c.network ?? '',
    connector: c.connector ?? '',
    stalls: c.stalls ? String(c.stalls) : '',
    notes: c.notes ?? '',
  };
}

/** Validate + convert the form into a ChargerInput, or return an error string. */
function buildInput(f: FormState): { input: ChargerInput } | { error: string } {
  const name = f.name.trim();
  if (!name) return { error: 'Name is required.' };
  const lat = Number(f.lat.trim());
  const lng = Number(f.lng.trim());
  if (f.lat.trim() === '' || !Number.isFinite(lat) || lat < -90 || lat > 90) {
    return { error: 'Latitude must be a number between -90 and 90.' };
  }
  if (f.lng.trim() === '' || !Number.isFinite(lng) || lng < -180 || lng > 180) {
    return { error: 'Longitude must be a number between -180 and 180.' };
  }
  const stalls = f.stalls.trim() === '' ? 0 : Number(f.stalls.trim());
  if (!Number.isFinite(stalls) || stalls < 0) {
    return { error: 'Stalls must be a non-negative number.' };
  }
  return {
    input: {
      name,
      active: f.active,
      lat,
      lng,
      address: f.address.trim(),
      network: f.network.trim(),
      connector: f.connector.trim(),
      stalls: Math.round(stalls),
      notes: f.notes.trim(),
    },
  };
}
